// Modal — accessible dialog on top of the existing overlay pattern.
// Handles: Escape to close, click-scrim to close, body-scroll lock, focus
// into the dialog on open, and role="dialog" + aria-modal + aria-labelledby.
// Renders via portal to <body> so it escapes stacking contexts.
//
//   <Modal open={open} onClose={() => setOpen(false)} title="Confirm">
//     <p>Are you sure?</p>
//     <Modal.Footer>
//       <Button variant="secondary" onClick={close}>Cancel</Button>
//       <Button variant="danger" onClick={confirm}>Delete</Button>
//     </Modal.Footer>
//   </Modal>

import { ReactNode, useEffect, useRef, HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Hide the header close button (still closes on Escape / scrim). */
  hideClose?: boolean;
  children: ReactNode;
}

function Modal({ open, onClose, title, size = 'md', hideClose, children }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useRef(`ui-modal-${Math.random().toString(36).slice(2, 8)}`).current;

  // Focus the dialog ONCE, on the open transition — keyed only on `open`.
  // This MUST NOT live in the effect below (which also depends on `onClose`):
  // callers pass an inline `onClose={() => …}`, so every parent re-render —
  // e.g. on each keystroke in a field inside the modal — hands us a new
  // onClose reference. If the focus call re-ran on that, it would yank focus
  // off the input the user is typing in back to the dialog, forcing a click
  // to type each character. Keying on `open` alone runs it only when opening.
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="ui-modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        className={['ui-modal', size !== 'md' ? `ui-modal--${size}` : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {(title || !hideClose) && (
          <div className="ui-modal__header">
            {title ? <h2 className="ui-modal__title" id={titleId}>{title}</h2> : <span />}
            {!hideClose && (
              <button type="button" className="ui-modal__close" onClick={onClose} aria-label="Close dialog">
                <Icon name="x" size={18} />
              </button>
            )}
          </div>
        )}
        <div className="ui-modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

Modal.Footer = function ModalFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={['ui-modal__footer', className].filter(Boolean).join(' ')} {...rest}>{children}</div>;
};

export default Modal;
